import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import { GridActionsCellItem } from '@mui/x-data-grid';

import CachedIcon from '@mui/icons-material/Cached';
import PendingIcon from '@mui/icons-material/Pending';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

import PageContainer from '../../component/PageContainer';
import HeaderPage from '../../component/HeaderPage';
import SmallHeaderPage from '../../component/SmallHeaderPage';
import SearchBox from '../../component/SearchBox';
import HeaderPageText from '../../component/HeaderPageText';
import DataTable from '../../component/DataTable';
import EvaluateServiceRequest from '../EvaluateServiceRequest';

import { apiFetchRequestService, apiHandleSaveEvaluation } from '../../api/axios';
import { loadRS } from '../../store/slice/homeSlice';
import { decryptPassword } from '../../utils/crypto';
import { buildEncodedToken } from '../../utils/auth';
import { filterList } from '../../utils/filterList';
import useAsyncAction from '../../hooks/useAsyncAction';

const PendingEvaluation = ({ isLoading, setIsLoading }) => {

  const auth = useSelector((state) => state.auth);
  const home = useSelector((state) => state.home);
  const dispatch = useDispatch();
  const [rs, setRs] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [evaluation, setEvaluation] = useState(false);
  const [srToEvaluation, setSRToEvaluation] = useState('');
  const [retSend, setRetSend] = useState('');

  const runAction = useAsyncAction(setErrorMsg);

  const getEncodedToken = useCallback(async () => {
    const password = await decryptPassword(auth.password, auth.user);
    return buildEncodedToken(auth.user, password);
  }, [auth.password, auth.user]);

  const columnsTable = useMemo(() => [
    { field: 'actions', type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          label='Avaliar'
          onClick={() => handleOpenEvaluation(params.row)}
          icon={
          <PendingIcon
            color='warning'
            variant='contained'
          />}
        />,
      ]
    },
    { field: 'number', headerName: 'Número', width: 130 },
    { field: 'openingDate',headerName: 'Data de Abertura', width: 150 },
    { field: 'serviceType', headerName: 'Tipo do Serviço', width: 200 },
    { field: 'department', headerName: 'Departamento', width: 250 },
    { field: 'asset', headerName: 'Bem', width: 250 },
    { field: 'closingDate',headerName: 'Data de Fechamento', width: 150 },
    { field: 'closingTime',headerName: 'Hora de Fechamento', width: 150 },
    { field: 'duration',headerName: 'Duração', width: 100 },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  useEffect(() => {

    fetchData();

  }, []);

  useEffect(() => {

    setRs(home.requestedServices);

  }, [home.requestedServices]);

  const fetchRequestService = useCallback(async () => {
    await runAction(async () => {
      const encodedToken = await getEncodedToken();
      const response = await apiFetchRequestService(encodedToken, auth);
      dispatch(loadRS({requestedServices: response.data.items}));
    }, 'Não foi possível consultar o servidor(SS Pendente)');
  }, [auth, dispatch, getEncodedToken, runAction]);

  const fetchData = useCallback(async () => {

    setIsLoading(() => true);

    try {
      await fetchRequestService();
    } finally {
      setIsLoading(() => false);
    }

  }, [fetchRequestService, setIsLoading]);

  const handleOpenEvaluation = (rs) => {

    setSRToEvaluation(() => rs);

    setEvaluation(() => true);

  };

  const handleCloseEvaluation = () => setEvaluation(() => false);

  const handleSaveEvaluation = useCallback(async (form) => {

    handleCloseEvaluation();
    setIsLoading(() => true);

    await runAction(async () => {
      const encodedToken = await getEncodedToken();
      await apiHandleSaveEvaluation(form, encodedToken, auth);
      setRetSend('Avaliação enviada');
      await fetchRequestService();
    }, 'Não foi possível enviar a avaliação');

    setIsLoading(() => false);

  }, [auth, fetchRequestService, getEncodedToken, runAction, setIsLoading]);

  const handleFilter = (event) => {
    setRs(filterList(event.target.value, home.requestedServices, ['number', 'openingDate', 'closingDate', 'closingTime', 'asset', 'serviceType', 'department']));
  };

  if(!home || ! home.requestedServices || !auth){
    return <Box></Box>;
    
  };

  return (

    <PageContainer>

      <EvaluateServiceRequest
        srToEvaluation={srToEvaluation}
        evaluation={evaluation}
        handleCloseEvaluation={handleCloseEvaluation}
        handleSaveEvaluation={handleSaveEvaluation}        
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
        open={errorMsg !== null && errorMsg !== undefined && errorMsg.trim() !== ''} autoHideDuration={3000} onClose={() => setErrorMsg('')}>
        <Alert 
          severity='error'
          onClose={() => setErrorMsg('')}>
          {`Ops! ${errorMsg}`}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
        open={retSend !== null && retSend !== undefined && retSend.trim() !== ''} autoHideDuration={3000} onClose={() => setRetSend('')}>
        <Alert 
          severity='success'
          onClose={() => setRetSend('')}>
          {retSend}
        </Alert>
      </Snackbar>
 
      <Box component='section' sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%'}}>
        
          <SmallHeaderPage>

            <HeaderPageText
              text= {`(${home.requestedServices.length})`}
              icon={<PendingActionsIcon/>}
            />
            
            <SearchBox
              label='Filtrar...'
              handleFilter={handleFilter}
              isLoading={isLoading}
            />

          </SmallHeaderPage>

          <Box sx={{display: {xs: 'flex', sm: 'flex', md: 'none', lg: 'none', xl: 'none'}, 
               flexDirection: 'column', 
               minHeight: '30vh', 
               justifyContent: 'space-evenly', 
               position: 'absolute', 
               bottom: 30, right: 30, bgcolor: 'transparent'}} >

              <Tooltip describeChild title='Recarregar solicitações de serviço'>
                <Fab size='medium' color='primary' onClick={fetchData}>
                  <CachedIcon sx={{ color: 'white'}}/>
                </Fab>
              </Tooltip>

          </Box>

          <HeaderPage>

            <Grid container>

              <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}>
                <HeaderPageText
                  text= {`Pendentes de avaliação (${home.requestedServices.length})`}
                  icon={<PendingActionsIcon/>}
                />

              </Grid>

              <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <SearchBox
                  label='Filtrar...'
                  handleFilter={handleFilter}
                  isLoading={isLoading}
                />
              </Grid>

              <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                <ButtonGroup variant='contained' disabled={isLoading}>
                  <Tooltip describeChild title='Recarregar solicitações de serviço'>
                    <Button
                      startIcon={<CachedIcon/>} 
                      onClick={fetchData}>
                      Recarregar
                    </Button>
                  </Tooltip>
                </ButtonGroup>

              </Grid>

            </Grid>

          </HeaderPage>

          <DataTable
            data={rs || []}
            isLoading={isLoading}
            columnsTable={columnsTable}
            pageSize={6}
            rowsPerPageOptions={6}
            fieldId='number'
            handleOpen={handleOpenEvaluation}
          />
        
      </Box>
      
    </PageContainer>

  );
};

export default PendingEvaluation;